import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@core-ui/Components/ui/skeleton';
import useSessionDetailsViewModel from '../hooks/useSessionDetailsViewModel';
import SessionHeader from '../components/SessionHeader';
import StatsCards from '../components/StatsCards';
import CallsTable from '../components/CallsTable';
import { IconInfoCircle } from '@tabler/icons-react';

function LoadingSkeleton (): React.JSX.Element {
  const skeletonClass = 'bg-muted';

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <Skeleton className={`h-9 w-9 rounded ${skeletonClass}`} />
            <div>
              <Skeleton className={`h-8 w-64 mb-2 ${skeletonClass}`} />
              <div className="flex items-center gap-4">
                <Skeleton className={`h-4 w-16 ${skeletonClass}`} />
                <Skeleton className={`h-4 w-24 ${skeletonClass}`} />
                <Skeleton className={`h-4 w-32 ${skeletonClass}`} />
                <Skeleton className={`h-4 w-28 ${skeletonClass}`} />
              </div>
            </div>
          </div>
          <Skeleton className={`h-9 w-24 rounded ${skeletonClass}`} />
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-border/60">
          <div className="flex items-center gap-4">
            <Skeleton className={`h-14 w-14 rounded-full ${skeletonClass}`} />
            <div>
              <Skeleton className={`h-5 w-32 mb-1 ${skeletonClass}`} />
              <Skeleton className={`h-4 w-48 ${skeletonClass}`} />
            </div>
          </div>
          <div className="flex gap-12">
            <div className="text-center">
              <Skeleton className={`h-9 w-12 mx-auto mb-1 ${skeletonClass}`} />
              <Skeleton className={`h-4 w-20 ${skeletonClass}`} />
            </div>
            <div className="text-center">
              <Skeleton className={`h-9 w-16 mx-auto mb-1 ${skeletonClass}`} />
              <Skeleton className={`h-4 w-24 ${skeletonClass}`} />
            </div>
            <div className="text-center">
              <Skeleton className={`h-9 w-12 mx-auto mb-1 ${skeletonClass}`} />
              <Skeleton className={`h-4 w-24 ${skeletonClass}`} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-start justify-between mb-3">
              <Skeleton className={`h-4 w-24 ${skeletonClass}`} />
              <Skeleton className={`h-6 w-6 rounded ${skeletonClass}`} />
            </div>
            <Skeleton className={`h-10 w-12 mb-2 ${skeletonClass}`} />
            <Skeleton className={`h-3 w-16 ${skeletonClass}`} />
          </div>
        ))}
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="p-4 border-b border-border/60">
          <Skeleton className={`h-5 w-12 ${skeletonClass}`} />
        </div>
        <div className="flex items-center gap-4 p-4 border-b border-border/60">
          <Skeleton className={`h-9 w-64 ${skeletonClass}`} />
          <Skeleton className={`h-9 w-[180px] ${skeletonClass}`} />
          <Skeleton className={`h-9 w-[160px] ${skeletonClass}`} />
        </div>
        <div className="bg-muted/30 px-4 py-3 border-b border-border/60">
          <div className="grid grid-cols-6 gap-4">
            <Skeleton className={`h-4 w-16 ${skeletonClass}`} />
            <Skeleton className={`h-4 w-20 ${skeletonClass}`} />
            <Skeleton className={`h-4 w-14 ${skeletonClass}`} />
            <Skeleton className={`h-4 w-14 ${skeletonClass}`} />
            <Skeleton className={`h-4 w-20 ${skeletonClass}`} />
            <Skeleton className={`h-4 w-12 ${skeletonClass}`} />
          </div>
        </div>
        <div>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="grid grid-cols-6 gap-4 px-4 py-3 border-b border-border/60 last:border-b-0">
              <div className="flex items-center gap-3">
                <Skeleton className={`h-9 w-9 rounded-full shrink-0 ${skeletonClass}`} />
                <Skeleton className={`h-4 w-24 ${skeletonClass}`} />
              </div>
              <div className="flex items-center">
                <Skeleton className={`h-6 w-20 rounded-full ${skeletonClass}`} />
              </div>
              <div className="flex items-center">
                <Skeleton className={`h-6 w-16 rounded-full ${skeletonClass}`} />
              </div>
              <div className="flex items-center">
                <Skeleton className={`h-4 w-28 ${skeletonClass}`} />
              </div>
              <div className="flex items-center">
                <Skeleton className={`h-4 w-12 ${skeletonClass}`} />
              </div>
              <div className="flex items-center">
                <Skeleton className={`h-4 w-16 ${skeletonClass}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ErrorState ({ onRetry, title, message, retryLabel }: { onRetry: () => void; title: string; message: string; retryLabel: string }): React.JSX.Element {
  return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {title}
        </h2>
        <p className="text-muted-foreground mt-2 mb-4">
          {message}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          {retryLabel}
        </button>
      </div>
    </div>
  );
}

function NoInfoState ({ title, message }: { title: string, message: string}) : React.JSX.Element {
  return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {title}
        </h2>
        <div className="p-2 mb-2 mt-2 rounded-lg bg-muted border border-border flex flex-col md:flex-row items-center gap-3">
          <IconInfoCircle className="min-w-[20px] min-h-[20px]" />
          <p className="text-muted-foreground">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SessionDetailsPage (): React.JSX.Element {
  const { t } = useTranslation();
  const {
    sessionDetails,
    filteredCalls,
    stats,
    contactsCalled,
    listContacts,
    fuegoLeadsCount,
    searchQuery,
    setSearchQuery,
    dispositionFilter,
    setDispositionFilter,
    agentFilter,
    setAgentFilter,
    actionFilter,
    setActionFilter,
    loadingState,
    isRefreshing,
    uniqueDispositions,
    uniqueAgents,
    uniqueActions,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalCalls,
    totalPages,
    loadRecordings,
    closeRecordings,
    selectedRecordings,
    recordingsLoadingState,
    goBack,
    downloadReport,
    isDownloadingReport,
    setOnClickFun,
  } = useSessionDetailsViewModel();

  // Lives here, not inside StatsCards: filtering from a card triggers a refetch
  // and the early return below swaps the whole page for the skeleton, so
  // StatsCards unmounts. This component stays mounted, so the tab you were
  // filtering from is still open when the rows come back.
  const [statsTab, setStatsTab] = useState('outcomes');

  // Only the first load gets the skeleton. A refetch keeps the session on
  // screen and dims the calls list instead, so filtering no longer flashes the
  // whole page.
  if (loadingState === 'LOADING' || loadingState === 'IDLE') {
    return <LoadingSkeleton />;
  }
  
  if ( sessionDetails === null || sessionDetails === undefined ) {
    return <NoInfoState
      title={t('dashboard.sessionDetailsPage.noCallsTitle')}
      message={t('dashboard.sessionDetailsPage.noCallsDescription')}
    />;
  }

  if (loadingState === 'ERROR') {
    return <ErrorState
      onRetry={() => {
        window.location.reload();
      }}
      title={t('dashboard.sessionDetailsPage.failedToLoad')}
      message={t('dashboard.sessionDetailsPage.errorMessage')}
      retryLabel={t('dashboard.sessionDetailsPage.tryAgain')}
    />;
  }

  const startDate = sessionDetails.session.startAtSession !== '' ? sessionDetails.session.startAtSession : new Date().toISOString();
  const listNames = [...new Set(sessionDetails.calls.map((c) => c.contactListName))].join(' / ');

  return (
    <div className="space-y-6">
      <SessionHeader
        session={sessionDetails.session}
        stats={stats}
        listName={listNames !== '' ? listNames : t('dashboard.sessionDetailsPage.fallbackName')}
        contactsCalled={contactsCalled}
        listContacts={listContacts}
        startDate={startDate}
        onBack={goBack}
        onDownloadReport={downloadReport}
        isDownloading={isDownloadingReport}
      />

      <StatsCards
        stats={stats}
        fuegoLeadCount={fuegoLeadsCount}
        onClickStat={setOnClickFun}
        activeTab={statsTab}
        onTabChange={setStatsTab}
      />

      <CallsTable
        calls={filteredCalls}
        sessionId={sessionDetails.session.callSessionId}
        isRefreshing={isRefreshing}
        searchQuery={searchQuery}
        dispositionFilter={dispositionFilter}
        agentFilter={agentFilter}
        actionFilter={actionFilter}
        uniqueDispositions={uniqueDispositions}
        uniqueAgents={uniqueAgents}
        uniqueActions={uniqueActions}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        totalCalls={totalCalls}
        totalPages={totalPages}
        onSearchChange={setSearchQuery}
        onDispositionChange={setDispositionFilter}
        onAgentChange={setAgentFilter}
        onActionChange={setActionFilter}
        onLoadRecording={loadRecordings}
        onCloseRecording={closeRecordings}
        selectedRecordings={selectedRecordings}
        recordingsLoadingState={recordingsLoadingState}
      />
    </div>
  );
}
